import { useMemo } from 'react';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { usePatientPrescriptionsQuery } from '@/entities/rx';
import { useUserPhrQuery } from '@/entities/user-phr';

import { toLabOrderSearchHits, toPhrSearchHits, toPrescriptionSearchHits } from './providers';
import { groupPatientGlobalSearchHits, searchPatientGlobalHits } from './search';
import type { PatientGlobalSearchGroup, SearchPatientGlobalHitsOptions } from './search';
import type { PatientGlobalSearchHit } from './types';

interface UsePatientGlobalSearchOptions {
  userId: string | undefined;
  query: string;
  limit?: number;
}

interface UsePatientGlobalSearchResult {
  isLoading: boolean;
  isError: boolean;
  indexedHits: PatientGlobalSearchHit[];
  results: PatientGlobalSearchHit[];
  groupedResults: PatientGlobalSearchGroup[];
}

export function usePatientGlobalSearch({
  userId,
  query,
  limit,
}: UsePatientGlobalSearchOptions): UsePatientGlobalSearchResult {
  const phrQuery = useUserPhrQuery(userId);
  const prescriptionsQuery = usePatientPrescriptionsQuery(userId, { synchronize: true });
  const { careState, status } = useCareStateDomain();

  const phrHits = useMemo(() => {
    if (!phrQuery.data) {
      return [];
    }
    return toPhrSearchHits(phrQuery.data.phr);
  }, [phrQuery.data]);

  const prescriptionHits = useMemo(() => {
    const prescriptions = prescriptionsQuery.data?.prescriptions ?? [];
    return toPrescriptionSearchHits(prescriptions);
  }, [prescriptionsQuery.data]);

  const labOrderHits = useMemo(() => {
    if (careState === null) {
      return [];
    }
    return toLabOrderSearchHits(careState.lab_orders);
  }, [careState]);

  const indexedHits = useMemo(
    () => [...phrHits, ...prescriptionHits, ...labOrderHits],
    [phrHits, prescriptionHits, labOrderHits]
  );

  const results = useMemo(() => {
    const options: SearchPatientGlobalHitsOptions = {
      hits: indexedHits,
      query,
      limit,
    };
    return searchPatientGlobalHits(options);
  }, [indexedHits, query, limit]);

  const groupedResults = useMemo(() => groupPatientGlobalSearchHits(results), [results]);

  return {
    isLoading: phrQuery.isLoading || prescriptionsQuery.isLoading || status === 'loading',
    isError: phrQuery.isError || prescriptionsQuery.isError || status === 'error',
    indexedHits,
    results,
    groupedResults,
  };
}
